/**
 * Provider URLs - Database-backed URL lookups
 * 
 * All provider URLs are stored in the provider_urls table.
 * This module provides helper functions to query them with caching.
 * 
 * MAINTENANCE: URLs are managed in the database, not in code.
 * Use the admin interface or SQL to update URLs.
 */

import { supabase } from '../lib/supabase';

// In-memory cache to avoid repeated DB calls
let urlCache = {
  data: null,
  loadedAt: null,
  maxAge: 5 * 60 * 1000, // 5 minutes
};

/**
 * Load all provider URLs from database (with caching)
 */
export const loadProviderUrls = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Return cached data if still fresh
  if (!forceRefresh && urlCache.data && urlCache.loadedAt && (now - urlCache.loadedAt < urlCache.maxAge)) {
    return urlCache.data;
  }
  
  try {
    const { data, error } = await supabase
      .from('provider_urls')
      .select('*')
      .eq('is_active', true);
    
    if (error) {
      console.error('Error loading provider URLs:', error);
      return urlCache.data || []; // Return stale cache on error
    }
    
    // Update cache
    urlCache.data = data || [];
    urlCache.loadedAt = now;
    
    return urlCache.data;
  } catch (err) {
    console.error('Failed to load provider URLs:', err);
    return urlCache.data || [];
  }
};

/**
 * Get URLs for a specific provider by type and name
 */
export const getProviderUrls = async (providerType, providerName) => {
  if (!providerName) return null;
  
  const allUrls = await loadProviderUrls();
  
  // Exact match first
  let match = allUrls.find(p => 
    p.provider_type === providerType && 
    p.provider_name === providerName
  );
  
  // Case-insensitive match
  if (!match) {
    const lowerName = providerName.toLowerCase();
    match = allUrls.find(p => 
      p.provider_type === providerType && 
      p.provider_name.toLowerCase() === lowerName
    );
  }
  
  // Partial match (for variations like "HBO Max" vs "Max")
  if (!match) {
    const lowerName = providerName.toLowerCase();
    match = allUrls.find(p => 
      p.provider_type === providerType && 
      (p.provider_name.toLowerCase().includes(lowerName) || 
       lowerName.includes(p.provider_name.toLowerCase()))
    );
  }
  
  return match || null;
};

/**
 * Get ISP URLs by provider name
 */
export const getISPUrls = async (providerName) => {
  const provider = await getProviderUrls('isp', providerName);
  if (!provider) return null;
  
  return {
    signup: provider.signup_url,
    account: provider.account_url,
    plans: provider.plans_url,
    support: provider.support_url,
    phone: provider.phone,
    hasChat: provider.has_chat,
  };
};

/**
 * Get streaming service URLs by name
 */
export const getStreamingUrls = async (serviceName) => {
  const provider = await getProviderUrls('streaming', serviceName);
  if (!provider) return null;
  
  return {
    signup: provider.signup_url,
    account: provider.account_url,
    plans: provider.plans_url,
    cancel: provider.cancel_url,
    support: provider.support_url,
  };
};

/**
 * Get membership URLs by name
 */
export const getMembershipUrls = async (membershipName) => {
  const provider = await getProviderUrls('membership', membershipName);
  if (!provider) return null;
  
  return {
    signup: provider.signup_url,
    account: provider.account_url,
    cancel: provider.cancel_url,
    support: provider.support_url,
  };
};

/**
 * Get bundle URLs by name
 */
export const getBundleUrls = async (bundleName) => {
  const provider = await getProviderUrls('bundle', bundleName);
  if (!provider) return null;
  
  return {
    signup: provider.signup_url,
    account: provider.account_url,
    support: provider.support_url,
  };
};

/**
 * Get credit card URLs by name
 */
export const getCreditCardUrls = async (cardName) => {
  const provider = await getProviderUrls('credit_card', cardName);
  if (!provider) return null;
  
  return {
    info: provider.signup_url,
    login: provider.account_url,
    benefits: provider.plans_url,
    support: provider.support_url,
  };
};

/**
 * Get all URLs of a specific type
 */
export const getUrlsByType = async (providerType) => {
  const allUrls = await loadProviderUrls();
  return allUrls.filter(p => p.provider_type === providerType);
};

/**
 * Get all URLs (for link verification)
 */
export const getAllUrls = async () => {
  const allUrls = await loadProviderUrls();
  const flatUrls = [];
  
  allUrls.forEach(provider => {
    const urlFields = ['signup_url', 'account_url', 'plans_url', 'cancel_url', 'support_url'];
    urlFields.forEach(field => {
      if (provider[field]) {
        flatUrls.push({
          id: provider.id,
          providerType: provider.provider_type,
          providerName: provider.provider_name,
          urlType: field.replace('_url', ''),
          url: provider[field],
          lastVerified: provider.last_verified_at,
        });
      }
    });
  });
  
  return flatUrls;
};

/**
 * Update last_verified_at for a provider (for automated checking)
 */
export const markUrlVerified = async (providerId, verifiedBy = 'automated') => {
  const { error } = await supabase
    .from('provider_urls')
    .update({
      last_verified_at: new Date().toISOString(),
      last_verified_by: verifiedBy,
    })
    .eq('id', providerId);
  
  if (error) {
    console.error('Error marking URL verified:', error);
    return false;
  }
  
  return true;
};

/**
 * Clear the cache (useful after admin updates)
 */
export const clearUrlCache = () => {
  urlCache.data = null;
  urlCache.loadedAt = null;
};